/**
 * Dealer-friendly labels for the v2 Publish Preview observability surface.
 *
 * Why this file exists
 * --------------------
 * The backend artifact contract uses internal identifiers like `fieldMapping`,
 * `listingRules`, `availability: 'unsupported'`, and `status: 'changed'`.
 * None of those are appropriate for dealer/support UI. Phase 4B gates ALL
 * v2-driven UI on these helpers so wording lives in one place.
 *
 * Anti-jargon rule
 * ----------------
 * Templates and components MUST NOT render the raw backend identifiers
 * (stage ids, availability tokens, decision-status tokens). Use these
 * helpers exclusively. The companion test
 * `tests/unit/utils/publishPreviewStageLabels.test.ts` enforces full
 * coverage of every observed identifier.
 */
import type {
  PublishDecisionRecord,
  PublishDecisionStatus,
  PublishObservedStage,
  PublishStageAvailability,
  PublishWebsiteImpactBucket,
} from '@/services/publishPreviewArtifactsService'

/**
 * Predictive section identifiers for the Publish-Flow-mirrored Preview
 * pipeline. `websitePublishOutcome` is Preview's derived final-state view (no
 * Website Refresh); `feedIntake` is intentionally absent.
 */
export type PreviewPipelineSection =
  | 'preProcessor'
  | 'fieldMapping'
  | 'inventorySync'
  | 'groups'
  | 'listingRules'
  | 'websitePublishOutcome'

/**
 * Predictive titles + descriptions for the six Preview pipeline sections,
 * ordered to mirror `PUBLISH_FLOW_BUSINESS_SECTIONS`. Copy stays predictive
 * ("will happen") because Preview simulates and never mutates the website.
 */
export function previewPipelineSectionLabel(section: PreviewPipelineSection | string): {
  title: string
  description: string
} {
  switch (section) {
    case 'preProcessor':
      return {
        title: 'Feed Preparation & Eligibility',
        description: 'Which vehicles will be eligible to move forward when you publish.',
      }
    case 'fieldMapping':
      return {
        title: 'Field Mapping',
        description: 'Which vehicle details will change on the website when you publish.',
      }
    case 'inventorySync':
      return {
        title: 'Inventory Sync',
        description: 'Which vehicles will enter or leave current inventory when you publish.',
      }
    case 'groups':
      return {
        title: 'Vehicle Groupings',
        description: 'Which SRP groupings vehicles will enter or leave when you publish.',
      }
    case 'listingRules':
      return {
        title: 'Publish Rules Evaluation',
        description: 'How publish rules will decide website visibility when you publish.',
      }
    case 'websitePublishOutcome':
      return {
        title: 'Website Publish Outcome',
        description: 'What the live website will look like after you publish.',
      }
    default:
      return {
        title: humanizeUnknownStage(String(section)),
        description: '',
      }
  }
}

/**
 * Predictive labels for the five website-impact categories. These mirror
 * Publish Flow's five Run Summary metrics exactly, with "will" phrasing, and
 * are NOT collapsed into made-visible/hidden.
 */
export function websiteImpactCategoryLabel(category: PublishWebsiteImpactBucket | string): string {
  switch (category) {
    case 'addedToWebsite':
      return 'Will be added to website'
    case 'removedFromWebsite':
      return 'Will be removed from website'
    case 'updatedOnWebsite':
      return 'Will be updated on website'
    case 'madeVisible':
      return 'Will be made visible'
    case 'hidden':
      return 'Will be hidden'
    default:
      return humanizeUnknownStage(String(category))
  }
}

/** Predictive one-line explanation for each website-impact category. */
export function websiteImpactCategoryDetail(category: PublishWebsiteImpactBucket | string): string {
  switch (category) {
    case 'addedToWebsite':
      return 'Vehicles that will newly appear in the live website inventory.'
    case 'removedFromWebsite':
      return 'Vehicles that will leave the live website inventory.'
    case 'updatedOnWebsite':
      return 'Still-live listings whose website data will change.'
    case 'madeVisible':
      return 'Vehicles whose website visibility will turn on.'
    case 'hidden':
      return 'Vehicles whose website visibility will turn off.'
    default:
      return ''
  }
}

/**
 * Map a backend stage id to the dealer-facing area name. Title case to
 * match the conventions on the existing `PreviewSummaryCard` ("Preview
 * Summary"), `Vehicle Groups`, and `Listing Visibility` headings.
 *
 * Falls back to a humanized form (e.g. `unknownStage` → `Unknown Stage`)
 * for any future stage the backend may add. The fallback is defensive,
 * NOT a license to render unknown ids verbatim.
 *
 * Note: `feedFieldRules` is labeled for VIN trail / sub-decision usage.
 * In the main Publish Summary UI it is nested under Vehicle data, not shown
 * as a top-level area.
 */
export function friendlyStageLabel(stage: PublishObservedStage | string): string {
  switch (stage) {
    case 'fieldMapping':
      return 'Vehicle information'
    case 'listingRules':
      return 'Website visibility'
    case 'groups':
      return 'Vehicle Groupings'
    case 'feedFieldRules':
      return 'Vehicle information rules'
    case 'preProcessor':
      return 'Inventory eligibility'
    case 'inventorySync':
      return 'Current inventory'
    case 'feedIntake':
      return 'Incoming feed check'
    default:
      return humanizeUnknownStage(stage)
  }
}

/**
 * Per-stage status copy when there is no decision record to show.
 *
 * The contract distinguishes two empty cases:
 *   - `available` + no record for this VIN  → "No changes detected..."
 *     (the area was simulated, but the artifact did not return this VIN)
 *   - `unsupported` / `deferred` / `notConfigured` → "Not evaluated yet."
 *     (the area was NOT simulated; the absence is by design)
 *
 * Surfacing both as the same message would be misleading, so this helper
 * always returns the case-appropriate copy. The optional `reason`
 * surfaces the manifest's `unsupportedReason` / `deferredReason` when
 * the backend provides one.
 */
export function friendlyAvailabilityCopy(
  availability: PublishStageAvailability,
  options: { hasRecord?: boolean; reason?: string } = {},
): { label: string; detail?: string } {
  switch (availability) {
    case 'available':
      if (options.hasRecord) {
        return { label: 'Changes detected.' }
      }
      return { label: 'No changes detected for this vehicle.' }
    case 'unsupported':
      return {
        label: 'Not evaluated yet.',
        detail: options.reason || 'This area is not yet supported in the detailed view.',
      }
    case 'deferred':
      return {
        label: 'Not evaluated yet.',
        detail: options.reason || 'Detailed evaluation is deferred for this preview.',
      }
    case 'notConfigured':
      return {
        label: 'Not evaluated yet.',
        detail: options.reason || 'Not configured for this dealer.',
      }
    default:
      return { label: 'Not evaluated yet.' }
  }
}

/**
 * Per-record outcome label for the VIN trail. Sentence case matches the
 * existing `VinResultsTable` copy ("Will be added to ...", "Will change").
 */
export function friendlyDecisionStatus(status: PublishDecisionStatus | string): string {
  switch (status) {
    case 'changed':
      return 'Will change'
    case 'unchanged':
      return 'No change'
    case 'skipped':
      return 'Skipped'
    case 'error':
      return 'Could not be evaluated'
    default:
      return 'Status unknown'
  }
}

export function friendlyPreProcessorPreviewDetail(record: PublishDecisionRecord): string {
  if (record.status === 'skipped') {
    return 'Vehicle will be removed before the next publish step.'
  }
  if (record.status === 'error') {
    return 'Inventory eligibility could not be checked for this vehicle.'
  }

  const after = isRecordObject(record.after) ? record.after : {}
  if (after.mode === 'sharedInventory') {
    return 'Vehicle will be prepared from the shared inventory file.'
  }
  if (after.dedupeWonOverSource === true) {
    return 'Inventory will use selected source before mapping.'
  }
  if (after.inputScope === 'groupSiteSource') {
    return 'Vehicle will be added from a connected inventory source.'
  }
  if (after.inputScope === 'groupSiteTarget') {
    return 'Inventory will use selected source from the target feed before mapping.'
  }
  return 'Inventory eligibility will change for this vehicle.'
}

export function friendlyFeedFieldRulesPreviewDetail(record: PublishDecisionRecord): string {
  if (record.status === 'error') {
    return 'Feed field rules could not be evaluated for this vehicle.'
  }

  if (record.status === 'skipped') {
    const reasonSkipped = record.reasons.find((item) => typeof item.explanation === 'string' && item.explanation.trim())
    return reasonSkipped?.explanation || 'Feed field rules were skipped for this vehicle.'
  }

  const reason = record.reasons.find((item) => typeof item.explanation === 'string' && item.explanation.trim())
  if (reason?.explanation) {
    return reason.explanation
  }

  const after = isRecordObject(record.after) ? record.after : {}
  const appliedRules = Array.isArray(after.appliedRules) ? after.appliedRules : []

  if (appliedRules.length > 0) {
    return `${appliedRules.length} ${appliedRules.length === 1 ? 'feed field rule will transform this vehicle' : 'feed field rules will transform this vehicle'}.`
  }

  const before = isRecordObject(record.before) ? record.before : {}
  const beforeAppliedRules = Array.isArray(before.appliedRules) ? before.appliedRules : []

  if (beforeAppliedRules.length > 0) {
    return `${beforeAppliedRules.length} ${beforeAppliedRules.length === 1 ? 'feed field rule will no longer transform this vehicle' : 'feed field rules will no longer transform this vehicle'}.`
  }

  return 'Feed field rules were evaluated for this vehicle.'
}

export function friendlyListingRulesPreviewDetail(record: PublishDecisionRecord): string {
  if (record.status === 'error') {
    return 'Website visibility could not be checked for this vehicle.'
  }
  if (record.status === 'skipped') {
    return 'Website visibility was skipped for this vehicle.'
  }

  const before = isRecordObject(record.before) ? record.before : {}
  const after = isRecordObject(record.after) ? record.after : {}

  if (before.decision === 'listed' && after.decision === 'delisted') {
    return 'Vehicle will be hidden after publish.'
  }
  if (before.decision === 'delisted' && after.decision === 'listed') {
    return 'Vehicle will appear online after publish.'
  }
  if (before.reason !== after.reason && (before.reason || after.reason)) {
    return 'Vehicle visibility reason will change after publish.'
  }

  return 'Website visibility was checked for this vehicle.'
}

/**
 * Predictive VIN-trail copy for the Inventory Sync projection. The record
 * carries an inventory-sync `classification` (and a human-readable
 * `reason.explanation`) rather than the generic `reasons[]` array. Wording is
 * predictive to match the rest of Preview ("will be added", "will leave").
 */
export function friendlyInventorySyncPreviewDetail(
  record: Pick<PublishDecisionRecord, 'status'> & {
    classification?: string
    reason?: { code?: string; explanation?: string }
  },
): string {
  const explanation = record.reason?.explanation
  if (typeof explanation === 'string' && explanation.trim()) {
    return explanation
  }
  switch (record.classification) {
    case 'will_be_added':
      return 'Vehicle will be added to current inventory after publish.'
    case 'will_leave':
      return 'Vehicle will leave current inventory after publish.'
    case 'remain_current':
      return 'Vehicle will remain in current inventory after publish.'
    case 'held_back':
      return 'Vehicle will not be admitted to current inventory after publish.'
    case 'error_missing':
      return 'Current inventory outcome could not be predicted for this vehicle.'
    default:
      return 'Current inventory outcome was evaluated for this vehicle.'
  }
}

/**
 * Defensive fallback when the backend introduces a stage id we don't
 * have copy for yet. We humanize the camelCase id so the UI stays
 * readable until the dictionary is updated. This path should be rare
 * and is covered by tests.
 */
function humanizeUnknownStage(stage: string): string {
  if (typeof stage !== 'string' || stage.length === 0) return 'Unknown area'
  const spaced = stage
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function isRecordObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}
